/**
 * Keeps an open session in step with the person's memberships.
 *
 * `OrgService` streams `users/{uid}` live, but roles come from the ID token,
 * which Firebase refreshes on its own only up to an hour later. So a person
 * added to an org had no role there (sidebar empty, guards bouncing, reads
 * denied), and a person removed from one kept working in it. This watches
 * both, and when they disagree forces ONE token refresh per users-doc
 * snapshot; once they agree it acts on what was lost (`membershipChange`):
 *
 *  - the last org went (and the person is not staff): sign out, and the
 *    sign-in card says "Your access to Acme was removed.";
 *  - an org went and others remain: `OrgService` has already moved the
 *    active org to one of them; the same sentence shows in the app;
 *  - the refresh failed because the session was revoked (what
 *    `syncOrgMemberClaims` does when a write ended the LAST membership;
 *    with another org left it does not revoke, and the case above
 *    applies): sign out, same sentence.
 *
 * Started at the root (`App` injects it), so it runs on every route,
 * before any route guard: `orgPermissionGuard` and `orgFeatureGuard` wait
 * on `settled()` before denying. On the join-request waiting screen it
 * only refreshes the token when the mirror gets ahead (an approval), next
 * to that screen's own refresh loop; it never signs out a person who never
 * had an org, who has nothing to lose here. The `pendingActivation` effect
 * in `App` is separate and unchanged.
 */
import { computed, DestroyRef, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { Router } from '@angular/router';

import { AuthService } from '../firebase/auth.service';
import { AccessNoticeService } from './access-notice.service';
import {
  accessRemovedMessage,
  inSync,
  isSessionRevoked,
  membershipChange,
  removedOrgs,
  type MembershipView,
} from './membership-change';
import { OrgService } from './org.service';

/**
 * How long `settled` waits for a claim that a refresh left behind the
 * mirror. A callable that adds a membership (`addOrgMember`, `createOrg`)
 * writes `orgIds` long BEFORE the claim: an invited org grants no role until
 * `markMemberActivated` (the `App` effect) has made it `active`, and only
 * then does `syncOrgMemberClaims` set the claim (#426). The trigger's own
 * mirror write, a few seconds after that, brings the next refresh. Past
 * this, a screen stops saying "Loading…" and shows what the token allows.
 */
export const CATCH_UP_MS = 10_000;

/** When an offline refresh is tried once more (no further retries). */
export const RETRY_MS = 5_000;

@Injectable({ providedIn: 'root' })
export class MembershipSessionService {
  private readonly auth = inject(AuthService);
  private readonly org = inject(OrgService);
  private readonly router = inject(Router);
  private readonly notices = inject(AccessNoticeService);

  /** The account the state below belongs to. */
  private uid: string | null = null;
  /** The first view of the session, then every view in which both agreed. */
  private baseline: MembershipView | null = null;
  /**
   * Org names seen while their doc was readable: a removed org's no longer
   * is. Kept across accounts on purpose: it only ever names an org the
   * current account had, and an org's name is the same for everyone.
   */
  private readonly names = new Map<string, string>();
  private ending = false;

  /** The users-doc snapshot a refresh was forced for: at most one each. */
  private readonly refreshedFor = signal<number | null>(null);
  private readonly refreshing = signal(false);
  /** A refresh came back still behind the mirror; see {@link CATCH_UP_MS}. */
  private readonly catchingUp = signal(false);
  private catchUpTimer: ReturnType<typeof setTimeout> | null = null;
  /** An offline refresh waits to be tried once more; see {@link RETRY_MS}. */
  private readonly retryPending = signal(false);
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Bumped by every reset. A refresh that settles after a sign-out or an
   * account change belongs to the session it started in and must not
   * write into the next one.
   */
  private session = 0;

  private readonly view = computed<MembershipView>(() => ({
    orgIds: this.org.orgIds(),
    orgRoles: Object.keys(this.auth.claims().orgRoles ?? {}),
  }));

  /**
   * False while the token may still be about to change: before the users
   * doc and the claims have loaded, and while they disagree until the
   * refresh for that snapshot is done and the claim had its chance to catch
   * up. A screen that shows "no access" for a missing role waits for this,
   * so a person whose claim is a refresh away sees "Loading…", not a denial.
   */
  readonly settled = computed(() => {
    if (!this.auth.claimsInitialized() || !this.org.userLoaded()) return false;
    const snapshot = this.org.userDocSeq();
    if (snapshot === 0) return true;
    if (inSync(this.view())) return true;
    return (
      this.refreshedFor() === snapshot &&
      !this.refreshing() &&
      !this.catchingUp() &&
      !this.retryPending()
    );
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.stopCatchUp();
      this.stopRetry();
    });

    effect(() => {
      const id = this.org.activeOrgDocId();
      const name = this.org.activeOrg()?.name;
      if (id && name) this.names.set(id, name);
    });

    effect(() => {
      const user = this.auth.user();
      if (user === undefined) return;
      if ((user?.uid ?? null) !== this.uid) untracked(() => this.reset(user?.uid ?? null));
      if (!user || !this.auth.claimsInitialized() || !this.org.userLoaded()) return;
      const snapshot = this.org.userDocSeq();
      if (snapshot === 0 || this.refreshing()) return;
      const view = this.view();
      const internalAdmin = this.auth.isInternalAdminAccount();
      untracked(() => this.react(snapshot, view, internalAdmin));
    });
  }

  private react(snapshot: number, view: MembershipView, internalAdmin: boolean): void {
    const baseline = (this.baseline ??= view);
    const change = membershipChange(baseline, view, { internalAdmin });
    if (change.kind !== 'refresh') this.stopCatchUp();
    switch (change.kind) {
      case 'refresh':
        if (this.refreshedFor() !== snapshot) void this.refresh(snapshot, baseline, view);
        return;
      case 'none':
        this.baseline = view;
        return;
      case 'switched':
        this.baseline = view;
        this.notices.showInApp(this.message(change.removed));
        return;
      case 'signed-out':
        this.baseline = view;
        void this.endSession(change.removed);
        return;
    }
  }

  private async refresh(
    snapshot: number,
    baseline: MembershipView,
    view: MembershipView,
    isRetry = false,
  ): Promise<void> {
    this.stopCatchUp();
    this.stopRetry();
    this.refreshedFor.set(snapshot);
    this.refreshing.set(true);
    const session = this.session;
    // Worked out now: a revoked session is signed out (and this state reset)
    // before the refresh rejects.
    const removed = removedOrgs(baseline, view.orgIds);
    try {
      await this.auth.refreshClaims();
      if (this.session !== session) return;
      if (!inSync(this.view())) this.startCatchUp();
    } catch (err) {
      if (isSessionRevoked(err)) {
        // Signed out meanwhile is this very case (the SDK signs a revoked
        // session out before rejecting); another account is not.
        if (this.session === session || this.uid === null) await this.endSession(removed);
      } else if (this.session === session && !isRetry) {
        // Offline and the like: once more shortly, then the next
        // users-doc change.
        this.scheduleRetry(snapshot, baseline, view);
      }
    } finally {
      if (this.session === session) this.refreshing.set(false);
    }
  }

  private scheduleRetry(snapshot: number, baseline: MembershipView, view: MembershipView): void {
    this.stopRetry();
    this.retryPending.set(true);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.retryPending.set(false);
      // A newer snapshot has taken over, or the token caught up on its own.
      if (this.org.userDocSeq() !== snapshot || this.refreshing() || inSync(this.view())) return;
      void this.refresh(snapshot, baseline, view, true);
    }, RETRY_MS);
  }

  private stopRetry(): void {
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.retryPending.set(false);
  }

  private async endSession(removed: readonly string[]): Promise<void> {
    if (this.ending) return;
    this.ending = true;
    try {
      this.notices.leaveForLogin(removed.length > 0 ? this.message(removed) : null);
      try {
        await this.auth.signOut();
      } catch {
        // Already signed out by the SDK (revoked session): nothing to undo.
      }
      await this.router.navigateByUrl('/login');
    } finally {
      this.ending = false;
    }
  }

  private message(removed: readonly string[]): string {
    return accessRemovedMessage(
      removed.map((id) => this.names.get(id)).filter((n): n is string => !!n),
    );
  }

  private startCatchUp(): void {
    this.stopCatchUp();
    this.catchingUp.set(true);
    this.catchUpTimer = setTimeout(() => {
      this.catchUpTimer = null;
      this.catchingUp.set(false);
    }, CATCH_UP_MS);
  }

  private stopCatchUp(): void {
    if (this.catchUpTimer !== null) clearTimeout(this.catchUpTimer);
    this.catchUpTimer = null;
    this.catchingUp.set(false);
  }

  private reset(uid: string | null): void {
    this.session++;
    this.uid = uid;
    this.baseline = null;
    // A notice belongs to the session it was about. (A sign-out this
    // service started has already moved it to the sign-in page, which a
    // sign-out must not drop; a new account signing in does.)
    this.notices.dismissInApp();
    if (uid !== null) this.notices.dropForLogin();
    this.refreshedFor.set(null);
    this.refreshing.set(false);
    this.stopCatchUp();
    this.stopRetry();
  }
}
